import React, {useState, useEffect, useRef, useCallback} from 'react';
import Player from 'react-player'
import Cursor from 'custom-cursor'
import {cloneDeep} from 'lodash'

import * as Tone from 'tone'
import A3 from "../src/Media/A3.mp3"
import A4 from "../src/Media/A4.mp3"
import B3 from "../src/Media/B3.mp3"
import D3 from "../src/Media/D3.mp3"
import D4 from "../src/Media/D4.mp3"
import Db4 from "../src/Media/Db4.mp3"
import E4 from "../src/Media/E4.mp3"
import G3 from "../src/Media/G3.mp3"
import Gb4 from "../src/Media/Gb4.mp3"

import './App.css';
import Coral from './Components/Coral';

function App() {
  const [isLoaded, setLoaded] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [octaveShift, setOctaveShift] = useState(0)
  const [sequencer, setSequencer] = useState([
    ["0:0:0", ["D3"]],
    ["0:0:1", ["Gb4"]], 
    ["0:0:2", ["D3"]], 
    ["0:0:3", ["D4"]], 
    ["0:0:4", ["D3"]], 
    ["0:0:5", ["E4"]], 
    ["0:0:6", ["D3", "Db4"]], 
    ["0:0:7", ["D4"]], 
])
  const [playingBeatIndex, setPlayingBeatIndex] = useState(-1)
  const [editingBeatIndex, setEditingBeatIndex] = useState(0)

  const [showInfo, setShowInfo] = useState(false)
  const [touchesBuffer, setTouchesBuffer] = useState(false)

  const sampler = useRef(null);
  const reverb = useRef(null);
  const distortion = useRef(null);
  const compressor = useRef(null)
  const volume = useRef(null)
  const seq = useRef(null)
  /* const isPlayingRef = useRef(isPlaying);
  const editingBeatIndexRef = useRef(editingBeatIndex); */

  const notes = { D3, G3, A3, B3, Db4, D4, E4, Gb4, A4 }
  let tuningArray = ["D3", "G3", "A3", "B3", "Db4", "D4", "E4", "Gb4", "A4" ]

  // For the custom cursor
  useEffect(() => {
    new Cursor({}).mount()
    return () => {
    }
  }, [])

  // Tone.js Initialization
  useEffect(() => {
    console.log("Loading!")
    sampler.current = new Tone.Sampler(
      notes,
      {
        onload: () => {
          console.log("Loaded!")
          setLoaded(true);
        }
      }
    ).toDestination()

    volume.current = new Tone.Volume(+6).toDestination()
    compressor.current = new Tone.Compressor({
      attack: .5,
      release: 1,
      threshold: -25, 
      ratio: 3
    })
    distortion.current = new Tone.Distortion({
      distortion: .15,
      wet: .5
    })

    compressor.current.connect(volume.current)

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {

      reverb.current = new Tone.Reverb({
        decay: .2,
        wet: .1
      })

      sampler.current.connect(distortion.current)
      distortion.current.connect(reverb.current)
      reverb.current.connect(compressor.current)

    } else {
      sampler.current.connect(distortion.current)
      distortion.current.connect(compressor.current)
    }

  }, []);

  // Tone.js Scheduling
  useEffect(() => {
    if (isLoaded) {
      seq.current = new Tone.Part((time, noteArray) => {

        if (noteArray) {
          sampler.current.context._context.resume() // necessary for Safari
          sampler.current.triggerAttack(noteArray, /* "8n", */ time);
        }

        Tone.Draw.schedule(() => {

          if (playingBeatIndex + 1 === sequencer.length) {
            setPlayingBeatIndex(0)
          } else {
            setPlayingBeatIndex(playingBeatIndex + 1)
          }

          if (noteArray) {
            noteArray.map(note => {
              let pad = document.querySelector(`button.Pad[data-note="${note}"]`)

              if (pad) {
                pad.className += " ghost"
                setTimeout(() => {
                  pad.className = pad.className.split(" ").filter(className => className !== "ghost").join(" ")
                }, 150)
              }

              return null
            })
          }
        }, time);
        // subdivisions are given as subarrays
      }, sequencer).start(0);
      seq.current.loop = true
      seq.current.loopEnd = '0:0:8'
      Tone.Draw.anticipation = 0.125
      Tone.Transport.bpm.value = 50;

      if (isPlaying) {
        Tone.Transport.start();
      } else {
        Tone.Transport.stop();
      }

      return () => {
        seq.current.dispose()
      }
    }
  }, [isLoaded, isPlaying, playingBeatIndex, sequencer])

  const handleKeyDown = useCallback(event => {
    try {
      // Find the pad and make it active
      switch (event.key) {
        case "1":
        case "2":
        case "3":
        case "4":
        case "5":
        case "6":
        case "7":
        case "8":
        case "9":
          let pad = [...document.querySelectorAll('button.Pad')].filter(pad => pad.innerHTML.toLowerCase() === event.key)[0]

          pad.className = pad.className + " active"

          let newSequencer = cloneDeep(sequencer)

          if (pad.className.includes("sequenced")) {
            newSequencer = newSequencer.map((beat, index) => {
              if (index === editingBeatIndex) {
                beat[1] = beat[1].filter(note => note !== pad.getAttribute('data-note'))
              }
              return beat
            })
            
            setSequencer(newSequencer)
          } else {
            newSequencer = newSequencer.map((beat, index) => {
              if (index === editingBeatIndex) {
                beat[1].push(pad.getAttribute('data-note'))
              }
              return beat
            })
            
            setSequencer(newSequencer)
          }
    
          // After a moment, make it inactive
          setTimeout(() => {
            pad.className = pad.className.split(" ").filter(className => className !== "active").join(" ")
          }, 150)

          // Trigger the sample set in this pad's "note" property
          if (isPlaying === false) {
            sampler.current.context._context.resume() // necessary for Safari
            sampler.current.triggerAttack(pad.getAttribute('data-note'));
          }

          break;
        case " ":
          console.log("Sequencer playing: ", isPlaying)
          if (isPlaying === true) {
            setIsPlaying(false)
          } else {
            setPlayingBeatIndex(-1)
            setIsPlaying(true)
          }
          break;
        case "ArrowRight":
          if (editingBeatIndex+ 1 === sequencer.length) {
            setEditingBeatIndex(0)
          } else {
            setEditingBeatIndex(editingBeatIndex + 1)
          }
          break
        case "ArrowLeft":
          if (editingBeatIndex - 1 < 0) {
            setEditingBeatIndex(sequencer.length - 1)
          } else {
            setEditingBeatIndex(editingBeatIndex- 1)
          }
          break;
        default:
          break;
      }
      
    } catch (error) { 
      console.error(error)
    }

    return null
  }, [sequencer, isPlaying, editingBeatIndex])
  // For adding/removing keyhandlers
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isPlaying, playingBeatIndex, sequencer, handleKeyDown])

  const handleClick = useCallback(note => {
    let pad = [...document.querySelectorAll('button.Pad')].filter(pad => pad.getAttribute('data-note') === note)[0]

    let newSequencer = cloneDeep(sequencer)

    if (pad.className.includes("sequenced")) {
      newSequencer = newSequencer.map((beat, index) => {
        if (index === editingBeatIndex) {
          beat[1] = beat[1].filter(note => note !== pad.getAttribute('data-note'))
          console.log(beat)
        }
        return beat
      })
      
      setSequencer(newSequencer)
    } else {
      newSequencer = newSequencer.map((beat, index) => {
        if (index === editingBeatIndex) {
          beat[1].push(pad.getAttribute('data-note'))
        }
        return beat
      })
      
      setSequencer(newSequencer)

      // Trigger the sample set in this pad's "note" property
      if (isPlaying === false) {
        sampler.current.context._context.resume() // necessary for Safari
        sampler.current.triggerAttack(note);
      }
      pad.className = pad.className + " active"

      // After a moment, make it inactive
      setTimeout(() => {
        pad.className = pad.className.split(" ").filter(className => className !== "active").join(" ")
      }, 150)
    }
  }, [sequencer, isPlaying, editingBeatIndex])

  const handleDrag = useCallback(event => {
    if (event.touches) {
      // First, turn the TouchList into an array we can map
      let touchList = [...event.touches]

      // If there's nothing in the touch buffer, fill it now
      if (touchesBuffer === false) { setTouchesBuffer(touchList) }

      touchList.map((touch, i) => {
        if (touchesBuffer.length > 0) {
          // Figure out which element we're currently touching and which we were previously touching
          let currentElement = document.elementFromPoint(touch.clientX, touch.clientY)
          let prevElement = null 

          try {
            prevElement = document.elementFromPoint(touchesBuffer[i].clientX, touchesBuffer[i].clientY)
          } catch (error) { console.error(error) }
          
          // If these elements differ...
          if (currentElement !== null && prevElement !== null && (currentElement === prevElement) === false) {
            //... and the current element is a Pad, play the Pad's note and write it to the touch buffer
            if (currentElement.className.includes("Pad")) {
              handleClick(currentElement.getAttribute('data-note'))
              setTouchesBuffer(touchList)
            }

            //... if the current elemnt is a Beat, focus it in edit mode and write it to the touch buffer
            if (currentElement.className.includes("Beat")) {
              let newEditingIndex = parseInt(currentElement.getAttribute('data-index'))
              /* console.log("It's the beat!", newEditingIndex) */
              setEditingBeatIndex(newEditingIndex)
              setTouchesBuffer(touchList)

              if (isPlaying === false) {
                sampler.current.context._context.resume() // necessary for Safari
                sampler.current.triggerAttack(sequencer[newEditingIndex][1]);
              }
            }
          }
        }

        return null
      })
    
    }
  }, [handleClick, touchesBuffer, isPlaying, sequencer])

  return (
    <div 
      id="wrapper"
      className={showInfo ? "active" : ""} 
    >
      <Coral
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        handleClick={handleClick}
        handleDrag={handleDrag}
        tuningArray={tuningArray}
        sequencer={sequencer}
        setSequencer={setSequencer}
        playingBeatIndex={playingBeatIndex}
        setPlayingBeatIndex={setPlayingBeatIndex}
        editingBeatIndex={editingBeatIndex}
        setEditingBeatIndex={setEditingBeatIndex}
        octaveShift={octaveShift}
        setOctaveShift={setOctaveShift}
        toneContext={Tone}
        isLoaded={isLoaded}
      />
      <div id="info" >
        <span 
          onClick={e => setShowInfo(!showInfo)}
          onTouchStart={e => setShowInfo(!showInfo)}
        >
          <h1 id="showInfo"><a href="https://thanksinp.art"><strong>coral</strong>.thanksinp.art</a></h1>
        </span>
        <p><em>Coral is a toy sequencer in the key of D.</em> You can play by touch or using keyboard keys. Press (or drag) the<svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><mask id="a" fill="#fff"><path fillRule="evenodd" clipRule="evenodd" d="M12.5 10a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zm-.625 0a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0z"/><path fillRule="evenodd" clipRule="evenodd" d="M12.5 2.5c0 .141-.012.28-.034.415.263.091.519.197.768.316a2.5 2.5 0 0 1 3.536 3.536c.118.248.224.504.315.767a2.5 2.5 0 1 1 0 4.931c-.091.264-.197.52-.316.768a2.5 2.5 0 0 1-3.536 3.536 7.435 7.435 0 0 1-.767.316 2.5 2.5 0 1 1-4.932 0 7.433 7.433 0 0 1-.768-.316 2.5 2.5 0 0 1-3.536-3.536 7.446 7.446 0 0 1-.315-.767 2.5 2.5 0 1 1 0-4.931 7.45 7.45 0 0 1 .316-.769A2.5 2.5 0 0 1 6.767 3.23a7.45 7.45 0 0 1 .767-.315A2.5 2.5 0 1 1 12.5 2.5zm-.625 0a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zm-4.16 13.986a6.82 6.82 0 0 1-.557-.224 2.5 2.5 0 0 0-3.42-3.42 6.844 6.844 0 0 1-.224-.556 2.5 2.5 0 0 0 0-4.572 6.83 6.83 0 0 1 .224-.556 2.5 2.5 0 0 0 3.42-3.42c.182-.083.367-.157.556-.224a2.5 2.5 0 0 0 4.572 0c.189.067.374.142.556.224a2.5 2.5 0 0 0 3.42 3.42c.082.182.157.367.224.556a2.5 2.5 0 0 0 0 4.572c-.067.189-.142.374-.224.556a2.5 2.5 0 0 0-3.42 3.42 6.834 6.834 0 0 1-.556.224 2.5 2.5 0 0 0-4.572 0zM6.874 5a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM15 6.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM6.875 15a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM15 16.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM4.375 10a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM17.5 11.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM11.875 17.5a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0z"/></mask><path fillRule="evenodd" clipRule="evenodd" d="M12.5 10a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zm-.625 0a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0z" fill="#FFB3CB"/><path fillRule="evenodd" clipRule="evenodd" d="M12.5 2.5c0 .141-.012.28-.034.415.263.091.519.197.768.316a2.5 2.5 0 0 1 3.536 3.536c.118.248.224.504.315.767a2.5 2.5 0 1 1 0 4.931c-.091.264-.197.52-.316.768a2.5 2.5 0 0 1-3.536 3.536 7.435 7.435 0 0 1-.767.316 2.5 2.5 0 1 1-4.932 0 7.433 7.433 0 0 1-.768-.316 2.5 2.5 0 0 1-3.536-3.536 7.446 7.446 0 0 1-.315-.767 2.5 2.5 0 1 1 0-4.931 7.45 7.45 0 0 1 .316-.769A2.5 2.5 0 0 1 6.767 3.23a7.45 7.45 0 0 1 .767-.315A2.5 2.5 0 1 1 12.5 2.5zm-.625 0a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zm-4.16 13.986a6.82 6.82 0 0 1-.557-.224 2.5 2.5 0 0 0-3.42-3.42 6.844 6.844 0 0 1-.224-.556 2.5 2.5 0 0 0 0-4.572 6.83 6.83 0 0 1 .224-.556 2.5 2.5 0 0 0 3.42-3.42c.182-.083.367-.157.556-.224a2.5 2.5 0 0 0 4.572 0c.189.067.374.142.556.224a2.5 2.5 0 0 0 3.42 3.42c.082.182.157.367.224.556a2.5 2.5 0 0 0 0 4.572c-.067.189-.142.374-.224.556a2.5 2.5 0 0 0-3.42 3.42 6.834 6.834 0 0 1-.556.224 2.5 2.5 0 0 0-4.572 0zM6.874 5a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM15 6.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM6.875 15a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM15 16.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM4.375 10a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM17.5 11.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM11.875 17.5a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0z" fill="#FFB3CB"/><path clipRule="evenodd" d="M12.5 10a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zm-.625 0a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0z" stroke="#FFB3CB" strokeWidth="2" mask="url(#a)"/><path clipRule="evenodd" d="M12.5 2.5c0 .141-.012.28-.034.415.263.091.519.197.768.316a2.5 2.5 0 0 1 3.536 3.536c.118.248.224.504.315.767a2.5 2.5 0 1 1 0 4.931c-.091.264-.197.52-.316.768a2.5 2.5 0 0 1-3.536 3.536 7.435 7.435 0 0 1-.767.316 2.5 2.5 0 1 1-4.932 0 7.433 7.433 0 0 1-.768-.316 2.5 2.5 0 0 1-3.536-3.536 7.446 7.446 0 0 1-.315-.767 2.5 2.5 0 1 1 0-4.931 7.45 7.45 0 0 1 .316-.769A2.5 2.5 0 0 1 6.767 3.23a7.45 7.45 0 0 1 .767-.315A2.5 2.5 0 1 1 12.5 2.5zm-.625 0a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zm-4.16 13.986a6.82 6.82 0 0 1-.557-.224 2.5 2.5 0 0 0-3.42-3.42 6.844 6.844 0 0 1-.224-.556 2.5 2.5 0 0 0 0-4.572 6.83 6.83 0 0 1 .224-.556 2.5 2.5 0 0 0 3.42-3.42c.182-.083.367-.157.556-.224a2.5 2.5 0 0 0 4.572 0c.189.067.374.142.556.224a2.5 2.5 0 0 0 3.42 3.42c.082.182.157.367.224.556a2.5 2.5 0 0 0 0 4.572c-.067.189-.142.374-.224.556a2.5 2.5 0 0 0-3.42 3.42 6.834 6.834 0 0 1-.556.224 2.5 2.5 0 0 0-4.572 0zM6.874 5a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM15 6.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM6.875 15a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM15 16.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM4.375 10a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zM17.5 11.875a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75zM11.875 17.5a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0z" stroke="#FFB3CB" strokeWidth="2" mask="url(#a)"/></svg>symbols to edit each step in your melody. Press the 
        <svg width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M.5.5h21v21H.5V.5zm1.37 1.364v7.073c.416-.365.92-.727 1.512-1.007a5.264 5.264 0 0 1 3.609-.353c1.39.348 2.899 1.256 4.508 2.956 1.488 1.572 2.78 2.301 3.844 2.567a3.888 3.888 0 0 0 2.685-.261c.752-.356 1.35-.9 1.767-1.37.132-.149.244-.288.335-.408V1.864H1.87zM.818 10.989A.147.147 0 0 0 .787 11c-.094.046-.101.184-.069.323a.89.89 0 0 0 .055.081l.097-.173v-.239l-.052-.003zm19.312 2.074c-.416.365-.92.727-1.512 1.007a5.264 5.264 0 0 1-3.609.353c-1.39-.348-2.899-1.256-4.508-2.956C9.013 9.895 7.721 9.166 6.657 8.9a3.888 3.888 0 0 0-2.685.261c-.752.356-1.35.9-1.767 1.37-.132.149-.244.288-.335.408v9.197h18.26v-7.073zm-4.365-5.067a1.01 1.01 0 0 0-.353.378 1.318 1.318 0 0 0-.134.625c0 .286.053.513.143.693a.987.987 0 0 0 .36.41c.146.092.315.142.526.142.312 0 .574-.114.81-.376l.386-.426.904 1.078-.306.324a2.44 2.44 0 0 1-1.83.784c-.466 0-.898-.11-1.277-.346a2.397 2.397 0 0 1-.863-.933l-.003-.006a2.832 2.832 0 0 1-.295-1.3c0-.473.098-.91.31-1.297l.002-.003c.213-.382.505-.688.873-.91l.004-.002A2.44 2.44 0 0 1 16.28 6.5c.806 0 1.478.29 1.926.916l.254.354-.565.537-.387.442-.604-.593.015-.104a.69.69 0 0 0-.23-.121l-.01-.004-.01-.003a1.343 1.343 0 0 0-.442-.057.886.886 0 0 0-.462.129zm-11.838 3.42c.448-.626 1.12-.916 1.926-.916.455 0 .879.107 1.259.331l.003.002c.369.222.66.528.873.91l.002.003c.212.387.31.824.31 1.296 0 .47-.094.907-.294 1.3l-.004.007c-.205.39-.494.704-.863.933a2.38 2.38 0 0 1-1.277.346 2.44 2.44 0 0 1-1.83-.784l-.306-.324.904-1.078.385.426c.237.262.499.376.812.376.21 0 .38-.05.524-.141a.987.987 0 0 0 .361-.411c.09-.18.144-.407.144-.694 0-.263-.051-.466-.135-.624a1.01 1.01 0 0 0-.352-.378.886.886 0 0 0-.462-.13c-.215 0-.357.026-.443.058l-.01.003-.01.004a.692.692 0 0 0-.23.12l.015.105-.604.593-.386-.443-.566-.536.254-.354z" fill="#FFB3CB"/></svg>
        symbol to play or stop your composition.</p>
      </div>
      <Player 
        className="video" 
        url="https://www.youtube.com/watch?v=Gu690aW0pRM" 
        loop={true} 
        playing={isPlaying}
      />
    </div>
  )
}

export default App;
